import {
  Box,
  FormControl,
  FormHelperText,
  Typography,
  Select,
  MenuItem,
} from '@mui/material';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import { FC } from 'react';
import { EnumDropdownProps } from 'src/models/FieldProps.interface';
import { unitErrorTextStyles } from '../StyledComponents/StyledUnitPicker';

export interface Option {
  label: string;
  value: string | number;
  disabled?: boolean;
}

interface FormikSelectFieldProps extends EnumDropdownProps {
  options: Option[];
  renderSelectedValue?: (value: string) => string;
}

const FormikSelectField: FC<FormikSelectFieldProps> = ({
  id,
  label,
  formikProps,
  options,
  renderSelectedValue,
  sx,
  errorState = false,
  height = '40px',
  width = '450px',
  borderColor = 'var(--primary-blue)',
  fontSize = '16px',
  fontWeight = '400',
  showLabel = true,
}) => {
  const hasError =
    formikProps.touched[id] && (Boolean(formikProps.errors[id]) || errorState);

  return (
    <Box mb={3}>
      {showLabel && (
        <Typography
          fontFamily="Outfit"
          fontWeight={fontWeight}
          fontSize={fontSize}
          color="var(--primary-navy)"
          mb={2}
        >
          {label}
        </Typography>
      )}
      <FormControl fullWidth error={hasError}>
        <Select
          id={id}
          name={id}
          value={formikProps.values[id]}
          onChange={formikProps.handleChange}
          onBlur={formikProps.handleBlur}
          displayEmpty
          IconComponent={ExpandMoreOutlined}
          sx={{
            height,
            width,
            bgcolor: 'var(--text-bg)',
            borderRadius: 1,
            '& .MuiSelect-select': {
              padding: '10px 14px',
              fontFamily: 'Outfit',
              fontSize: fontSize,
              fontWeight: fontWeight,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: hasError ? 'var(--error)' : 'var(--border)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: borderColor,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: borderColor,
            },
            ...(sx || {}),
          }}
          renderValue={(selected) => {
            if (!selected) return `Select ${label}`;
            return renderSelectedValue
              ? renderSelectedValue(selected)
              : options.find((o) => o.value === selected)?.label;
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: '230px',
                borderRadius: 2,
                border: '1px solid var(--border)',
                bgcolor: 'var(--text-bg)',
              },
            },
          }}
        >
          {options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              sx={{
                fontFamily: 'Outfit',
                fontSize: fontSize,
                '&.Mui-selected': {
                  backgroundColor: 'var(--inactive-btn)',
                  color: 'var(--primary-blue)',
                },
                '&.Mui-disabled': {
                  opacity: 0.6,
                  color: 'var(--subtext)',
                },
              }}
            >
              {option.label}
              {option.disabled ? ' (Inactive)' : ''}
            </MenuItem>
          ))}
        </Select>
        {hasError && (
          <FormHelperText sx={{ ...unitErrorTextStyles }}>
            <Box
              component="i"
              className="bx bxs-error-circle"
              sx={{ fontSize: '16px', mr: 0.8 }}
            />
            {formikProps.errors[id]}
          </FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

export default FormikSelectField;
